import { createContext, useContext, useState } from 'react'


  type reportOptions = {
    reasons: {
      key: string,
      title: string
    }[]
    comment: string
    notifiedPeople: {
      userId: number,
      userName: string,
      email: string
    }[]
    resolution?: string
  }
  
  type ReportOptionsContextType = {
    reportOptions: reportOptions
    updateReportOptions: <Key extends keyof reportOptions>(key: Key, value: reportOptions[Key] | null) => void
    setReportOptions: (newReportOptions: reportOptions) => void
    toggleReportOptionsValue: <Key extends keyof reportOptions>(key: Key, value: reportOptions[Key] extends Array<infer U> ? U : never, matchBy: keyof (reportOptions[Key] extends Array<infer U> ? U : never)) => void
  }
  
  const ReportOptionsContext = createContext<ReportOptionsContextType | undefined>(undefined)
  
  export function ReportOptionsProvider({ children }: { children: React.ReactNode }) {
    const [reportOptions, setReportOptionsState] = useState<reportOptions>({
      reasons: [],
      notifiedPeople: [],
      comment: '',
      resolution: undefined
    })
  
    function updateReportOptions<Key extends keyof reportOptions>(key: Key, value: reportOptions[Key] | null) {
      setReportOptionsState(prev => {
        if (value === null) {
          const { [key]: _, ...rest } = prev
          return rest as reportOptions
        }
        return { ...prev, [key]: value }
      })
    }
  
    function setReportOptions(newFilters: reportOptions) {
      setReportOptionsState(newFilters)
    }

    function toggleReportOptionsValue<Key extends keyof reportOptions>(
      key: Key,
      value: reportOptions[Key] extends Array<infer U> ? U : never,
      matchBy: keyof (reportOptions[Key] extends Array<infer U> ? U : never) = 'id' as any
    ) {
      setReportOptionsState(prev => {
        const currentArray = (prev[key] as unknown as any[]) || []
    
        const exists = currentArray.some((item: any) => item[matchBy] === (value as any)[matchBy])
    
        const newArray = exists
          ? currentArray.filter((item: any) => item[matchBy] !== (value as any)[matchBy])
          : [...currentArray, value]
    
        return {
          ...prev,
          [key]: newArray
        }
      })
    }
  
    return (
      <ReportOptionsContext.Provider value={{ reportOptions, updateReportOptions, setReportOptions, toggleReportOptionsValue }}>
        {children}
      </ReportOptionsContext.Provider>
    )
  }
  
  export function useReportOptions() {
    const context = useContext(ReportOptionsContext)
    if (!context) {
      throw new Error('useFilters must be used within a FiltersProvider')
    }
    return context
  }
  